import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as EmotionThemeProvider, Global, css } from '@emotion/react';
import { lightTheme } from './theme/lightTheme';
import { darkTheme } from './theme/darkTheme';
const ThemeContext = createContext({
    theme: lightTheme,
    mode: 'light',
    toggleMode: () => { },
});
export function useTheme() {
    return useContext(ThemeContext);
}
const globalStyles = (theme) => css `
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: ${theme.typography.fontFamily.sans};
    background-color: ${theme.colors.bg.primary};
    color: ${theme.colors.text.primary};
    line-height: ${theme.typography.lineHeight.normal};
    transition: background-color 250ms ease, color 250ms ease;
    min-height: 100vh;
  }

  a {
    color: ${theme.colors.text.link};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  img,
  video {
    max-width: 100%;
    height: auto;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
  }

  input,
  textarea,
  select {
    font-family: inherit;
  }

  :focus-visible {
    outline: 2px solid ${theme.colors.brand.primary};
    outline-offset: 2px;
  }

  ::selection {
    background-color: ${theme.colors.brand.primary};
    color: ${theme.colors.brand.primaryText};
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.border.strong};
    border-radius: 9999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.text.tertiary};
  }
`;
export function ThemeProvider({ children, defaultMode = 'dark' }) {
    const [mode, setMode] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('incubs-theme-mode');
            if (saved === 'light' || saved === 'dark')
                return saved;
            if (window.matchMedia('(prefers-color-scheme: dark)').matches)
                return 'dark';
        }
        return defaultMode;
    });
    const theme = mode === 'dark' ? darkTheme : lightTheme;
    const toggleMode = useCallback(() => {
        setMode((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem('incubs-theme-mode', next);
            return next;
        });
    }, []);
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', mode);
    }, [mode]);
    return (_jsx(ThemeContext.Provider, { value: { theme, mode, toggleMode }, children: _jsxs(EmotionThemeProvider, { theme: theme, children: [_jsx(Global, { styles: globalStyles(theme) }), children] }) }));
}
//# sourceMappingURL=ThemeProvider.js.map